<template>
  <footer class="the-footer">
    <div class="container">
      <div class="inner">
        <div class="md:px-2">
          <NuxtLink
            class="logo"
            :class="logoHeight ? logoHeight : 'h-8'"
            to="/"
          >
            <NuxtImg v-no-right-click :src="companyInfo.logo" alt="logo" />
          </NuxtLink>
          <p class="description">
            {{ t(companyInfo.about_company) }}
          </p>
          <div class="inline-block mb-[40px]">
            <span class="company-link-title">
              {{ t("footer.payment") + ":" }}
            </span>
            <NuxtImg
              v-no-right-click
              src="/accept-payment.svg"
              alt="accept payment"
              class="h-5"
            />
          </div>
        </div>
        <div class="flex flex-col md:flex-row gap-5 md:gap-24">
          <div class="links-list">
            <h6 class="title">
              {{ companyInfo.name }}
            </h6>
            <ul>
              <li
                v-for="(item, index) in footerConfig.public.footer.items"
                :key="index"
                class="link-item"
              >
                <NuxtLink :to="localePath(item.target)">
                  {{ t(item?.title) }}
                </NuxtLink>
              </li>
            </ul>
          </div>
          <div class="max-md:mt-5">
            <div
              v-if="
                hasSocialMedia ||
                info?.whatsapp_number ||
                info?.telephones ||
                info?.email
              "
            >
              <h5 class="text-mobile-heading-5 md:text-mobile-heading-5 mb-5">
                {{ t("getHelp") }}
              </h5>
              <a
                v-if="info?.whatsapp_number"
                :href="`https://wa.me/${replaceSpace(info?.whatsapp_number)}`"
                target="_blank"
                class="mb-5 flex items-center text-mobile-heading-6 md:text-heading-6"
              >
                <icon-whatsapp class="!text-[16px] mr-2" />
                <span dir="ltr">{{ info?.whatsapp_number }}</span>
              </a>
              <a
                v-if="info?.telephones?.length"
                :href="`tel:${info?.telephones[0]}`"
                class="mb-5 flex items-center text-mobile-heading-6 md:text-heading-6"
              >
                <icon-call class="!text-[16px] mr-2" />
                <span dir="ltr">{{ info?.telephones[0] }}</span>
              </a>
              <a
                v-if="info?.email"
                :href="`mailto:${info?.email}`"
                class="mb-5 md:mb-6 flex items-center text-mobile-heading-6 md:text-heading-6"
              >
                <icon-email class="!text-[16px] mr-2" />
                {{ info?.email }}
              </a>
              <div v-if="hasSocialMedia" class="mb-14 md:mb-10">
                <TheFooterSocialMedias :socials="companyInfo.social_medias" />
                <div v-if="companyInfo.outer_links" class="flex flex-col mt-10">
                  <span
                    class="text-gray-4 text-mobile-paragraph-2 md:text-paragraph-2 mb-3 title-font"
                  >
                    {{ t("footer.weAreListed") }}:
                  </span>
                  <div class="flex flex-col md:flex-row">
                    <NuxtLink
                      v-for="outerLink in companyInfo.outer_links"
                      :key="outerLink?.title"
                      :to="outerLink.target"
                      target="_blank"
                      class="flex items-center gap-2.5 mb-2 md:mb-0 md:me-5 cursor-pointer"
                    >
                      <NuxtImg
                        v-no-right-click
                        :src="outerLink.icon"
                        :alt="outerLink?.title"
                        width="30"
                      />
                      <span
                        class="text-gray-4 text-mobile-paragraph-2 md:text-paragraph-2 title-font"
                      >
                        {{ outerLink?.title }}
                      </span>
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="rights">
        {{ t("allRightsReserved") }}
      </div>
    </div>
  </footer>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import customFetch from "../../composables/customFetch";
import type { ICompanyInfo } from "../../types/general";
import TheFooterSocialMedias from "./TheFooterSocialMedias.vue";
import { useAsyncData, useRuntimeConfig } from "nuxt/app";

const localePath = useLocalePath();
const config = useRuntimeConfig() as unknown as RuntimeConfiguration;
const { t } = useI18n();

defineProps({ type: { type: Number, default: 1 } });

function replaceSpace(item: string) {
  if (!item) return "";
  return item.replace(/\s+/g, "");
}

const footerConfig = ref<RuntimeConfiguration>(config);

const logoHeight = footerConfig.value.public.footer.logoHeightSize ?? null;

const companyInfo = {
  name: footerConfig.value.public.name,
  about_company: footerConfig.value.public.footer.text,
  logo: footerConfig.value.public.footer.logo,
  social_medias: footerConfig.value.public.footer.socialLinks,
  outer_links: footerConfig.value.public.footer.outerLinks,
};

const hasSocialMedia = computed(() => {
  return Object.keys(companyInfo.social_medias).length;
});

const { data: info } = await useAsyncData<ICompanyInfo>("about", async () =>
  customFetch("api/company-info ")
);
</script>

<style scoped>
.the-footer {
  @apply bg-footer-background;
}
.the-footer .inner {
  @apply pb-8 md:pb-11 pt-[40px] md:pt-[90px] text-paragraph-2
  flex flex-col md:flex-row justify-between items-start
   text-footer-text-color;
}
.the-footer .inner .logo {
  @apply inline-flex mb-5 block w-full;
}
.the-footer .inner .logo img {
  @apply h-full w-auto rounded-none object-contain max-md:max-w-[60%];
}
.the-footer .inner .description {
  @apply text-mobile-heading-5 lg:text-heading-5 md:max-w-[17rem] mb-6;
}

.the-footer .inner .links-list {
  @apply md:px-2 xs:max-md:w-full;
}

.the-footer .inner .links-list .title {
  @apply md:mb-5 py-5 md:py-0
 flex justify-between items-center
 xs:max-md:cursor-pointer
 text-mobile-heading-5 md:text-heading-5;
}

.the-footer .inner .links-list .link-item {
  @apply text-mobile-heading-6 lg:text-heading-6 mb-5;
}

.the-footer .inner .description,
.the-footer .inner .links-list .title .the-footer .inner .link-item {
  @apply text-footer-text-color;
}
.the-footer .inner .company-link-title {
  @apply block text-mobile-heading-6 lg:text-heading-6 text-footer-text-color mb-2.5;
}
.the-footer .inner .b2b-button {
  @apply border border-primary-1 w-max px-3 py-2 rounded-md text-primary-1 block text-paragraph-2;
}

.the-footer .rights {
  @apply text-footer-text-color pb-10 md:pb-5 text-paragraph-2;
}
</style>
